import React from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  // Team member data
  const teamMembers = [
    {
      id: 1,
      name: 'Dr. Sarah Chen',
      role: 'Founder & Lead AI Educator',
      bio: 'Former AI researcher at DeepMind with a Ph.D. in Machine Learning. Sarah founded NeuraAcademy to make AI education accessible to everyone.',
      image: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    {
      id: 2,
      name: 'Michael Rodriguez',
      role: 'Chief Learning Officer',
      bio: 'With 10+ years in educational technology, Michael designs our learning experiences to be engaging, practical, and effective.',
      image: 'https://randomuser.me/api/portraits/men/45.jpg'
    },
    {
      id: 3,
      name: 'Amara Patel',
      role: 'AI Ethics Specialist',
      bio: 'Amara ensures our content addresses the ethical implications of AI and promotes responsible development practices.',
      image: 'https://randomuser.me/api/portraits/women/67.jpg'
    },
    {
      id: 4,
      name: 'David Kim',
      role: 'Technical Content Director',
      bio: 'Former senior engineer at Google, David brings real-world expertise to our technical curriculum and code examples.',
      image: 'https://randomuser.me/api/portraits/men/22.jpg'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary-500 to-secondary-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              About NeuraAcademy
            </h1>
            <p className="text-lg md:text-xl text-primary-50">
              We're on a mission to democratize AI education and empower the next generation of AI practitioners through accessible, high-quality learning.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  NeuraAcademy was founded in 2025 with a simple yet ambitious goal: to create the most effective learning platform for artificial intelligence.
                </p>
                <p>
                  We noticed a gap in the educational landscape. While AI was becoming increasingly influential in every industry, quality education in this field remained inaccessible to many - either hidden behind expensive paywalls or diluted in free but superficial content.
                </p>
                <p>
                  Our founder, Dr. Sarah Chen, having spent years in AI research, decided to build a platform that would combine academic rigor with practical, hands-on learning - all delivered in a format that respects the learner's time and preferred learning style.
                </p>
                <p>
                  The result is NeuraAcademy: a text-based learning platform that focuses on clarity, depth, and practical application. Unlike video-centric platforms, our approach allows learners to move at their own pace, easily search and reference content, and focus on implementation rather than passive consumption.
                </p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="The NeuraAcademy team" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300">
              At NeuraAcademy, we believe that understanding AI is becoming as fundamental as computer literacy. Our mission is threefold:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 dark:text-primary-400 font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-3">
                Accessibility
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                Make high-quality AI education accessible to everyone, regardless of their background or resources. We believe knowledge should empower, not exclude.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 dark:text-primary-400 font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-3">
                Practical Mastery
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                Develop courses that balance theoretical understanding with practical implementation. Our learners don't just know AI—they can build with it.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 dark:text-primary-400 font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 dark:text-white mb-3">
                Ethical Focus
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                Promote responsible AI development by integrating ethical considerations into every aspect of our curriculum. We train not just capable, but conscientious practitioners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300">
              The passionate minds behind NeuraAcademy bringing expertise from AI research, education, and industry
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div key={member.id} className="bg-gray-50 dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm">
                <div className="h-64 overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-semibold text-xl text-gray-900 dark:text-white">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 dark:text-primary-400 text-sm mt-1 mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm">
                  <h3 className="font-heading font-semibold text-lg text-gray-900 dark:text-white mb-3">
                    Quality Over Quantity
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    We prioritize depth and clarity in our content, focusing on high-quality learning experiences rather than an overwhelming volume of material.
                  </p>
                </div>
                
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm">
                  <h3 className="font-heading font-semibold text-lg text-gray-900 dark:text-white mb-3">
                    Learner-Centered Design
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Our platform is designed with the learner's experience as the central consideration, from content structure to interface design.
                  </p>
                </div>
                
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm">
                  <h3 className="font-heading font-semibold text-lg text-gray-900 dark:text-white mb-3">
                    Continuous Improvement
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    We constantly update our content to reflect the latest developments in AI and incorporate feedback from our community.
                  </p>
                </div>
                
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm">
                  <h3 className="font-heading font-semibold text-lg text-gray-900 dark:text-white mb-3">
                    Inclusive Community
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    We foster a supportive learning environment where diverse perspectives are valued and everyone feels welcome.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-6">
                Our Values
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                At NeuraAcademy, our values guide everything we do—from curriculum development to platform design to community building. These principles shape our decisions and reflect our commitment to excellence in AI education.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                We believe that education is most effective when it respects the learner's intelligence, time, and individual learning style. That's why we've created a platform that allows for self-paced, focused, and reference-friendly learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
            Join Us on This Journey
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Whether you're looking to start a career in AI, enhance your existing skills, or simply understand the technology that's shaping our future, NeuraAcademy is here to guide you every step of the way.
          </p>
          <Link 
            to="/register" 
            className="px-8 py-3 bg-white text-primary-600 font-medium rounded-lg hover:bg-gray-100 transition-colors inline-block"
          >
            Start Learning Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About; 